import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import { X, AlertTriangle, CheckCircle, Info } from 'lucide-react';
import ClinicalCatalogQuickForm from './ClinicalCatalogQuickForm';
import LocalStorageService from '../../services/storage/LocalStorageService';
import { Tooltip } from '../Common/ReusableUI';

function fourAtDraftKey(residentId) {
  return `quickform_draft_delirium-4at_${residentId ?? 'none'}`;
}

/**
 * Quick capture for catalog-aligned assessments (demo / Phase 1).
 * Full field engines: see Compliance.md §6-7 + Resident.md §11
 */

function interpret4AT(total) {
  if (total === 0) return { label: 'Unlikely delirium or significant cognitive impairment', tone: 'ok' };
  if (total >= 1 && total <= 3) return { label: 'Possible cognitive impairment — follow up', tone: 'watch' };
  return { label: 'Possible delirium ± cognitive impairment — urgent clinical review', tone: 'urgent' };
}

const QUESTION_DEFAULTS = { alertness: 'normal', amt4: 'zero', attention: 'good', acute: 'no' };
const TOTAL_QUESTIONS = 4;

const HELP_TEXT = {
  alertness: 'Observe the patient\u2019s level of alertness and responsiveness',
  amt4: 'Ask: age, date of birth, current location, current year',
  attention: 'Ask the patient to recite months backwards from December',
  acute: 'Has there been a sudden change in mental status?',
};

function FourATForm({ residentName, residentId, onClose, onComplete }) {
  const [alertness, setAlertness] = useState('normal');
  const [amt4, setAmt4] = useState('zero');
  const [attention, setAttention] = useState('good');
  const [acute, setAcute] = useState('no');
  const [touched, setTouched] = useState({ alertness: false, amt4: false, attention: false, acute: false });
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [validationError, setValidationError] = useState('');
  const [lastAutoSave, setLastAutoSave] = useState(null);
  const skipFourSave = useRef(false);

  const markTouched = useCallback((field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    setValidationError('');
  }, []);

  useEffect(() => {
    setAlertness('normal');
    setAmt4('zero');
    setAttention('good');
    setAcute('no');
    setTouched({ alertness: false, amt4: false, attention: false, acute: false });
    setShowConfirmation(false);
    setValidationError('');
    setLastAutoSave(null);
    const raw = LocalStorageService.get(fourAtDraftKey(residentId), null);
    if (raw && typeof raw === 'object') {
      if (raw.alertness) { setAlertness(raw.alertness); setTouched(prev => ({ ...prev, alertness: true })); }
      if (raw.amt4) { setAmt4(raw.amt4); setTouched(prev => ({ ...prev, amt4: true })); }
      if (raw.attention) { setAttention(raw.attention); setTouched(prev => ({ ...prev, attention: true })); }
      if (raw.acute) { setAcute(raw.acute); setTouched(prev => ({ ...prev, acute: true })); }
      if (raw.updatedAt) setLastAutoSave(raw.updatedAt);
    }
    skipFourSave.current = true;
  }, [residentId]);

  useEffect(() => {
    if (skipFourSave.current) {
      skipFourSave.current = false;
      return;
    }
    const t = setTimeout(() => {
      const now = new Date().toISOString();
      LocalStorageService.set(fourAtDraftKey(residentId), {
        alertness, amt4, attention, acute, updatedAt: now,
      });
      setLastAutoSave(now);
      if (process.env.NODE_ENV === 'development') {
        // eslint-disable-next-line no-console
        console.debug('[ClinicalAssessmentQuickForm] 4AT draft autosaved', { residentId });
      }
    }, 450);
    return () => clearTimeout(t);
  }, [alertness, amt4, attention, acute, residentId]);

  const scores = useMemo(() => {
    const a = alertness === 'abnormal' ? 4 : 0;
    const b = amt4 === 'one' ? 1 : amt4 === 'two' ? 2 : 0;
    const c = attention === 'poor' ? 1 : attention === 'untestable' ? 2 : 0;
    const d = acute === 'yes' ? 4 : 0;
    return { a, b, c, d, total: a + b + c + d };
  }, [alertness, amt4, attention, acute]);

  const interpretation = useMemo(() => interpret4AT(scores.total), [scores.total]);

  const answeredCount = useMemo(() => {
    return [
      touched.alertness || alertness !== QUESTION_DEFAULTS.alertness,
      touched.amt4 || amt4 !== QUESTION_DEFAULTS.amt4,
      touched.attention || attention !== QUESTION_DEFAULTS.attention,
      touched.acute || acute !== QUESTION_DEFAULTS.acute,
    ].filter(Boolean).length;
  }, [touched, alertness, amt4, attention, acute]);

  const allAnswered = answeredCount === TOTAL_QUESTIONS;

  const handlePreSubmit = (e) => {
    e.preventDefault();
    if (!allAnswered) {
      setValidationError('Please complete all questions before submitting.');
      return;
    }
    setShowConfirmation(true);
  };

  const handleFinalSubmit = () => {
    const payload = {
      tool: '4AT',
      slug: 'delirium-4at',
      scores,
      interpretation: interpretation.label,
      residentName,
      savedAt: new Date().toISOString(),
    };
    if (process.env.NODE_ENV === 'development') {
      // eslint-disable-next-line no-console
      console.debug('[ClinicalAssessmentQuickForm] 4AT saved', payload);
    }
    LocalStorageService.remove(fourAtDraftKey(residentId));
    onComplete?.(payload);
    onClose();
  };

  if (showConfirmation) {
    return (
      <div className="space-y-4">
        <p className="text-sm text-gray-600 font-medium">Review before submitting — {residentName || 'resident'}</p>

        <div
          className={`rounded-xl p-4 border-2 ${
            interpretation.tone === 'urgent'
              ? 'bg-red-50 border-red-200 text-red-900'
              : interpretation.tone === 'watch'
                ? 'bg-amber-50 border-amber-200 text-amber-900'
                : 'bg-green-50 border-green-200 text-green-900'
          }`}
        >
          <div className="flex items-start gap-2">
            {interpretation.tone === 'urgent' ? (
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
            ) : (
              <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
            )}
            <div>
              <p className="font-bold text-lg">4AT Score: {scores.total}</p>
              <p className="text-sm mt-1">{interpretation.label}</p>
            </div>
          </div>
        </div>

        <div className="text-sm space-y-1 text-gray-700 bg-gray-50 rounded-lg p-3">
          <div className="flex justify-between"><span>1. Alertness</span><span className="font-medium">{scores.a}</span></div>
          <div className="flex justify-between"><span>2. AMT4</span><span className="font-medium">{scores.b}</span></div>
          <div className="flex justify-between"><span>3. Attention</span><span className="font-medium">{scores.c}</span></div>
          <div className="flex justify-between"><span>4. Acute change</span><span className="font-medium">{scores.d}</span></div>
        </div>

        {interpretation.tone === 'urgent' && (
          <p className="text-red-700 text-sm font-medium flex items-center gap-1">
            <AlertTriangle className="w-4 h-4" /> Escalate to clinical review per local protocol.
          </p>
        )}

        <div className="flex gap-2 pt-2">
          <button type="button" onClick={() => setShowConfirmation(false)} className="mf-btn-secondary flex-1">
            Back to Edit
          </button>
          <button type="button" onClick={handleFinalSubmit} className="mf-btn-primary flex-1">
            Confirm &amp; Save
          </button>
        </div>
      </div>
    );
  }

  return (
    <form onSubmit={handlePreSubmit} className="space-y-4">
      <p className="text-sm text-gray-600">
        4AT rapid delirium screen — {residentName ? <strong>{residentName}</strong> : 'resident'} (spec: Compliance.md §6 / assessment #15).{' '}
        <span className="text-indigo-700 text-xs font-medium">Draft autosave (local)</span>
      </p>

      {/* Progress Bar */}
      <div className="space-y-1">
        <div className="flex justify-between text-xs text-gray-500">
          <span>Question {Math.min(answeredCount + 1, TOTAL_QUESTIONS)} of {TOTAL_QUESTIONS}</span>
          <span>{answeredCount}/{TOTAL_QUESTIONS} answered</span>
        </div>
        <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
          <div
            className="h-full rounded-full transition-all duration-300 bg-blue-500"
            style={{ width: `${(answeredCount / TOTAL_QUESTIONS) * 100}%` }}
          />
        </div>
      </div>

      {validationError && (
        <div className="flex items-center gap-2 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2" role="alert">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          {validationError}
        </div>
      )}

      <fieldset className="space-y-2">
        <legend className="mf-label">1. Alertness</legend>
        <p className="text-xs text-gray-500 -mt-1 flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
          {HELP_TEXT.alertness}
        </p>
        <select
          value={alertness}
          onChange={(e) => { setAlertness(e.target.value); markTouched('alertness'); }}
          className="w-full border border-surface-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
          aria-describedby="fourat-interpretation"
        >
          <option value="normal">Normal (0)</option>
          <option value="mild">Mild sleepiness, normal after &lt;10s (0)</option>
          <option value="abnormal">Clearly abnormal (4)</option>
        </select>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="mf-label">2. AMT4 (age, DOB, place, year)</legend>
        <p className="text-xs text-gray-500 -mt-1 flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
          {HELP_TEXT.amt4}
        </p>
        <select
          value={amt4}
          onChange={(e) => { setAmt4(e.target.value); markTouched('amt4'); }}
          className="w-full border border-surface-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
          aria-describedby="fourat-interpretation"
        >
          <option value="zero">All correct (0)</option>
          <option value="one">1 error (1)</option>
          <option value="two">≥2 errors or untestable (2)</option>
        </select>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="mf-label">3. Attention (months backwards)</legend>
        <p className="text-xs text-gray-500 -mt-1 flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
          {HELP_TEXT.attention}
        </p>
        <select
          value={attention}
          onChange={(e) => { setAttention(e.target.value); markTouched('attention'); }}
          className="w-full border border-surface-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
          aria-describedby="fourat-interpretation"
        >
          <option value="good">7+ months correct (0)</option>
          <option value="poor">Started but &lt;7 or refused (1)</option>
          <option value="untestable">Untestable (2)</option>
        </select>
      </fieldset>

      <fieldset className="space-y-2">
        <legend className="mf-label">4. Acute change or fluctuating course</legend>
        <p className="text-xs text-gray-500 -mt-1 flex items-start gap-1">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0 text-gray-400" />
          {HELP_TEXT.acute}
        </p>
        <select
          value={acute}
          onChange={(e) => { setAcute(e.target.value); markTouched('acute'); }}
          className="w-full border border-surface-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 min-h-[44px]"
          aria-describedby="fourat-interpretation"
        >
          <option value="no">No (0)</option>
          <option value="yes">Yes (4)</option>
        </select>
      </fieldset>

      <div
        id="fourat-interpretation"
        role="status"
        aria-live="polite"
        className={`rounded-xl p-4 border-2 ${
          interpretation.tone === 'urgent'
            ? 'bg-red-50 border-red-200 text-red-900'
            : interpretation.tone === 'watch'
              ? 'bg-amber-50 border-amber-200 text-amber-900'
              : 'bg-green-50 border-green-200 text-green-900'
        }`}
      >
        <div className="flex items-start gap-2">
          {interpretation.tone === 'urgent' ? (
            <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
          ) : (
            <CheckCircle className="w-5 h-5 shrink-0 mt-0.5" />
          )}
          <div>
            <Tooltip content="Scores 1-3 suggest cognitive impairment. Scores ≥4 suggest delirium and require urgent clinical review." position="right">
              <p className="font-bold text-lg cursor-help underline decoration-dotted">Total: {scores.total}</p>
            </Tooltip>
            <p className="text-sm mt-1">{interpretation.label}</p>
            {interpretation.tone === 'urgent' && (
              <Tooltip content="Score ≥4 meets escalation threshold — notify senior nurse and GP per facility protocol" position="bottom">
                <p className="mf-field-error mt-2 mb-0 cursor-help underline decoration-dotted">Escalate to clinical review per local protocol.</p>
              </Tooltip>
            )}
          </div>
        </div>
      </div>

      {lastAutoSave && (
        <p className="text-xs text-gray-400 text-center">
          Draft auto-saved at {new Date(lastAutoSave).toLocaleTimeString()}
        </p>
      )}

      <div className="flex gap-2 pt-2">
        <button type="button" onClick={onClose} className="mf-btn-secondary flex-1">
          Cancel
        </button>
        <button type="submit" className="mf-btn-primary flex-1">
          Save Assessment
        </button>
      </div>
    </form>
  );
}

/**
 * @param {{ slug: string | null, catalogItem: { name: string } | null, residentName?: string, onClose: () => void, onComplete?: (payload: object) => void }} props
 */
export default function ClinicalAssessmentQuickFormModal({
  slug,
  catalogItem,
  residentName = '',
  residentId,
  onClose,
  onComplete,
}) {
  if (!slug || !catalogItem) return null;

  return (
    <div
      className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/50"
      role="dialog"
      aria-modal="true"
      aria-labelledby="quick-form-title"
      onClick={onClose}
    >
      <div
        className="mf-card shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto p-6 border-gray-200"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-between items-start gap-3 mb-4">
          <h3 id="quick-form-title" className="mf-heading-sm pr-8">
            Start: {catalogItem.name}
          </h3>
          <button
            type="button"
            aria-label="Close"
            onClick={onClose}
            className="touch-target p-2 rounded-lg hover:bg-surface-100 inline-flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {slug === 'delirium-4at' ? (
          <FourATForm residentName={residentName} residentId={residentId} onClose={onClose} onComplete={onComplete} />
        ) : (
          <ClinicalCatalogQuickForm
            slug={slug}
            catalogItem={catalogItem}
            residentName={residentName}
            residentId={residentId}
            onClose={onClose}
            onComplete={onComplete}
          />
        )}
      </div>
    </div>
  );
}
